import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/meta";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Reveal, MaskedLine, Overline } from "@/components/motion";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const weekLabel = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? iso : `Week of ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
};
const BEANS = "https://images.unsplash.com/photo-1553292218-4892c2e7e1ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxiYXJpc3RhJTIwcG91cmluZyUyMGVzcHJlc3NvJTIwcm9hc3RlZCUyMGNvZmZlZSUyMGJlYW5zJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3OTIyMjEwMDJ8MA&ixlib=rb-4.1.0&q=85";

export default function LocationPage() {
  const { slug } = useParams();
  const [loc, setLoc] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [missing, setMissing] = useState(false);

  usePageMeta(
    loc ? `${loc.name} | Give & Grind` : "Give & Grind — Coffee that funds charity",
    loc
      ? `${loc.cups.toLocaleString()} cups poured, ${fmt(loc.raised)} raised for ${loc.charity} — 22p of every cup donated.`
      : "A better coffee machine. 22p of every cup goes to a charity you choose."
  );

  useEffect(() => {
    api.get(`/locations/${slug}`)
      .then((r) => {
        setLoc(r.data);
        api.get(`/locations/${slug}/weekly`).then((w) => setWeeks(w.data)).catch(() => {});
      })
      .catch(() => setMissing(true));
  }, [slug]);

  return (
    <div data-testid="location-page" className="bg-[#F7F5F0] min-h-screen">
      <Nav dark />
      {missing ? (
        <section className="min-h-[70vh] flex items-center justify-center px-5 pt-24" data-testid="location-missing">
          <div className="text-center max-w-md">
            <p className="font-display font-extrabold text-4xl text-[#0B1E17]">Brewing soon.</p>
            <p className="mt-4 text-sm text-[#52665E] leading-relaxed">
              This machine isn't on the map yet. The Bean Ladder launches with machine #01 — check back soon.
            </p>
            <a href="/ladder" className="mt-8 inline-flex rounded-full bg-[#0F2E23] text-[#F7F5F0] font-bold px-6 py-3 text-sm hover:bg-[#1B5E46] transition-colors">
              Back to the Bean Ladder
            </a>
          </div>
        </section>
      ) : !loc ? (
        <p className="pt-40 text-center text-sm text-[#52665E]" data-testid="location-loading">Pouring the details…</p>
      ) : (
        <>
          <section className="pt-32 pb-16 bg-[#0B1D16] text-[#FAF9F6] grain relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-[#1B5E46]/30 blur-[110px] pointer-events-none" />
            <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
              <MaskedLine delay={0.1}>
                <span className={`inline-flex text-xs font-mono-brand font-semibold uppercase tracking-[0.2em] rounded-full px-4 py-2 border ${
                  loc.status === "live" ? "text-[#34D399] border-[#34D399]/40" : "text-[#D97706] border-[#D97706]/40"
                }`}>
                  {loc.status === "live" ? "Live machine" : "Coming soon"}
                </span>
              </MaskedLine>
              <h1 className="mt-6 font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="location-name">
                <MaskedLine delay={0.25}>{loc.name}</MaskedLine>
              </h1>
              <p className="mt-4 text-base text-[#FAF9F6]/60">{loc.city}{loc.host ? ` · hosted by ${loc.host}` : ""}</p>

              <div className="mt-12 grid grid-cols-3 gap-4 max-w-2xl" data-testid="location-stats">
                <div className="bg-[#13271F] border border-white/10 rounded-2xl p-5">
                  <p className="font-display font-extrabold text-2xl sm:text-3xl text-[#FAF9F6]">{loc.cups.toLocaleString()}</p>
                  <p className="text-xs text-[#FAF9F6]/55 mt-1">cups poured</p>
                </div>
                <div className="bg-[#13271F] border border-white/10 rounded-2xl p-5">
                  <p className="font-display font-extrabold text-2xl sm:text-3xl text-[#D97706]">{fmt(loc.raised)}</p>
                  <p className="text-xs text-[#FAF9F6]/55 mt-1">raised so far</p>
                </div>
                <div className="bg-[#13271F] border border-white/10 rounded-2xl p-5">
                  <p className="font-display font-extrabold text-2xl sm:text-3xl text-[#34D399]">22p</p>
                  <p className="text-xs text-[#FAF9F6]/55 mt-1">of this cup, donated</p>
                </div>
              </div>
              <p className="mt-6 text-xs font-mono-brand text-[#FAF9F6]/40 uppercase tracking-[0.14em]">
                Updated every Monday from machine sales reports
              </p>
            </div>
          </section>

          <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid lg:grid-cols-2 gap-10">
            <Reveal>
              <div className="bg-white rounded-3xl border border-[#E2DDD3] p-8 sm:p-10 h-full" data-testid="location-charity-card">
                <Overline>Why this charity</Overline>
                {loc.logo_url && (
                  <img src={`${BACKEND}${loc.logo_url}`} alt={`${loc.charity} logo`} data-testid="location-charity-logo"
                    className="mt-5 h-14 w-auto object-contain rounded-lg" />
                )}
                <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl text-[#0B1E17]">{loc.charity}</h2>
                <p className="mt-4 text-base text-[#52665E] leading-relaxed">
                  {loc.charity_blurb || `${loc.name} chose ${loc.charity} as their Give & Grind partner. Every cup poured here sends 22p their way.`}
                </p>
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#34D399]/10 text-[#1B5E46] text-xs font-bold px-4 py-2">
                  Registered UK charity partner
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="relative rounded-3xl overflow-hidden border border-[#E2DDD3] h-full min-h-[300px]">
                <img src={BEANS} alt="Freshly roasted coffee beans" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D16]/85 via-[#0B1D16]/20 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <p className="text-xs font-mono-brand uppercase tracking-[0.2em] text-[#34D399]">In the hopper</p>
                  <p className="mt-2 font-display font-bold text-xl text-[#FAF9F6]">Specialty-grade, freshly roasted beans — ground to order in every cup.</p>
                </div>
              </div>
            </Reveal>
          </section>

          <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16" data-testid="location-weekly-section">
            <Reveal>
              <div className="bg-white rounded-3xl border border-[#E2DDD3] p-8 sm:p-10">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <Overline>Live tracking</Overline>
                    <h2 className="mt-3 font-display font-bold text-2xl text-[#0B1E17]">Weekly impact updates</h2>
                  </div>
                  <p className="text-xs font-mono-brand text-[#52665E] uppercase tracking-[0.14em]">Posted every Monday</p>
                </div>
                {weeks.length === 0 ? (
                  <p className="mt-8 text-sm text-[#52665E]" data-testid="location-weekly-empty">
                    No updates yet — the first weekly report lands the Monday after this machine is installed.
                  </p>
                ) : (
                  <div className="mt-8 divide-y divide-[#E2DDD3]" data-testid="location-weekly-list">
                    {weeks.map((w) => (
                      <div key={w.id} className="py-4 flex flex-wrap items-center justify-between gap-3" data-testid={`weekly-row-${w.week_of}`}>
                        <p className="text-sm font-semibold text-[#0B1E17]">{weekLabel(w.week_of)}</p>
                        <div className="flex items-center gap-6">
                          <p className="text-sm text-[#52665E]"><span className="font-display font-bold text-[#0B1E17]">{w.cups.toLocaleString()}</span> cups</p>
                          <p className="font-display font-extrabold text-lg text-[#D97706] w-24 text-right">{fmt(w.raised)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Reveal>
          </section>

          <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
            <Reveal>
              <div className="bg-[#0F2E23] text-[#FAF9F6] rounded-3xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 grain relative overflow-hidden">
                <div>
                  <p className="font-display font-bold text-2xl">Want this in your space?</p>
                  <p className="mt-2 text-sm text-[#FAF9F6]/60 max-w-md">Full service in London &amp; the Home Counties — supply and support anywhere else in the UK. You pick the charity either way.</p>
                </div>
                <a href="/#enquire" data-testid="location-cta"
                  className="shrink-0 rounded-full bg-[#D97706] text-white font-bold px-7 py-3.5 text-sm hover:bg-[#F59E0B] transition-colors duration-300">
                  Get a machine
                </a>
              </div>
            </Reveal>
          </section>
        </>
      )}
      <Footer />
    </div>
  );
}
